import React, { useMemo, useState } from "react";
import {
  countedShiftsInWeek,
  formatChkHours,
  shiftHours,
  sumFactHours,
  weekdayIndexInWeek,
} from "./chkHours";

const DAY_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const LOW_HOURS = 20;
const HIGH_HOURS = 40;

function hoursStatus(hours) {
  if (hours < LOW_HOURS) return "low";
  if (hours > HIGH_HOURS) return "high";
  return "ok";
}

function statusLabel(status) {
  if (status === "low") return "мало";
  if (status === "high") return "много";
  return "норма";
}

function statusTitle(status) {
  if (status === "low") return `меньше ${LOW_HOURS} ч`;
  if (status === "high") return `больше ${HIGH_HOURS} ч`;
  return `${LOW_HOURS}–${HIGH_HOURS} ч`;
}

function formatDayTitle(weekDates, index, label, hasShift) {
  const date = weekDates[index];
  const datePart = date
    ? `, ${String(date).slice(8, 10)}.${String(date).slice(5, 7)}`
    : "";
  return `${label}${datePart}${hasShift ? ": смена" : ": выходной"}`;
}

const ScheduleChkSummary = ({
  data,
  restaurantId,
  userModal = [],
  dataScheduleDays,
  weekDates = [],
}) => {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("hours");
  const [sortDir, setSortDir] = useState("asc");
  const [groupByPost, setGroupByPost] = useState(false);

  const ownEmployeeIds = useMemo(() => {
    if (!restaurantId) return null;
    return new Set(
      (Array.isArray(userModal) ? userModal : [])
        .filter((user) => user.restaurant_id === restaurantId)
        .map((user) => Number(user.id)),
    );
  }, [userModal, restaurantId]);

  const ftIds = useMemo(() => {
    const ids = new Set();
    const rows = Array.isArray(dataScheduleDays)
      ? dataScheduleDays
      : Object.values(dataScheduleDays || {});
    rows.forEach((row) => {
      if (row?.length === 9 && row[7]?.id) ids.add(Number(row[7].id));
    });
    return ids;
  }, [dataScheduleDays]);

  const rows = useMemo(() => {
    const users = (data?.users || []).filter((user) =>
      restaurantId ? user.restaurant_id === restaurantId : true,
    );
    const shifts = countedShiftsInWeek(data?.shifts, weekDates).filter((shift) => {
      if (ownEmployeeIds?.size) return ownEmployeeIds.has(Number(shift.employee));
      return true;
    });

    return users.map((user) => {
      const userShifts = shifts.filter(
        (shift) => Number(shift.employee) === Number(user.id),
      );
      const days = [false, false, false, false, false, false, false];
      let hours = 0;
      userShifts.forEach((shift) => {
        hours += shiftHours(shift);
        const day = weekdayIndexInWeek(shift.date, weekDates);
        if (day >= 0 && day <= 6) days[day] = true;
      });
      return {
        id: user.id,
        name: `${user.surname ?? ""} ${user.name ?? ""}`.trim(),
        post: user.post_name || "Без должности",
        shiftCount: userShifts.length,
        hours,
        factHours: sumFactHours(user.fact_hours),
        days,
        isFT: ftIds.has(Number(user.id)),
        status: hoursStatus(hours),
      };
    });
  }, [data, restaurantId, ownEmployeeIds, ftIds, weekDates]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = rows.filter((row) => {
      if (!needle) return true;
      return `${row.name} ${row.post}`.toLowerCase().includes(needle);
    });
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === "name") {
        return a.name.localeCompare(b.name, "ru") * dir;
      }
      if (sortKey === "shifts") {
        return (a.shiftCount - b.shiftCount) * dir;
      }
      if (sortKey === "fact") {
        if (a.factHours !== b.factHours) return (a.factHours - b.factHours) * dir;
        return a.name.localeCompare(b.name, "ru");
      }
      if (a.hours !== b.hours) return (a.hours - b.hours) * dir;
      return a.name.localeCompare(b.name, "ru");
    });
    return list;
  }, [rows, query, sortKey, sortDir]);

  const stats = useMemo(() => {
    const inSchedule = rows.filter((row) => row.shiftCount > 0);
    const totalHours = inSchedule.reduce((sum, row) => sum + row.hours, 0);
    const totalFact = rows.reduce((sum, row) => sum + row.factHours, 0);
    const lowCount = inSchedule.filter((row) => row.status === "low").length;
    return {
      people: inSchedule.length,
      totalHours,
      totalFact: Math.round(totalFact * 10) / 10,
      average: inSchedule.length
        ? Math.round((totalHours / inSchedule.length) * 10) / 10
        : 0,
      lowCount,
    };
  }, [rows]);

  const maxHours = Math.max(HIGH_HOURS, ...rows.map((row) => row.hours), 1);

  const grouped = useMemo(() => {
    if (!groupByPost) return null;
    const map = new Map();
    filtered.forEach((row) => {
      if (!map.has(row.post)) map.set(row.post, []);
      map.get(row.post).push(row);
    });
    return Array.from(map.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], "ru"),
    );
  }, [filtered, groupByPost]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const sortMark = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const renderRow = (row) => (
    <tr key={row.id} className={`chk-summary-row is-${row.status}`}>
      <td className="chk-summary-employee">
        <div className="chk-summary-name">
          {row.name}
          {row.isFT && <span className="schedule-chk-ft-badge">FT</span>}
        </div>
        <div className="chk-summary-post">{row.post}</div>
      </td>
      <td className="text-center">{row.shiftCount}</td>
      <td>
        <div className="chk-summary-hours">
          <div className="chk-summary-hours-bar">
            <span
              style={{ width: `${Math.min(100, (row.hours / maxHours) * 100)}%` }}
            />
          </div>
          <strong>{row.hours}</strong>
        </div>
      </td>
      <td className="text-center chk-summary-fact">
        {row.factHours > 0 ? formatChkHours(row.factHours) : "—"}
      </td>
      <td className="text-center">
        <span
          className={`chk-summary-badge is-${row.status}`}
          title={statusTitle(row.status)}
        >
          {statusLabel(row.status)}
        </span>
      </td>
      <td>
        <div className="chk-summary-days" title="Дни со сменой">
          {DAY_LABELS.map((label, index) => (
            <span
              key={label}
              className={`chk-summary-dot ${row.days[index] ? "is-on" : ""}`}
              title={formatDayTitle(
                weekDates,
                index,
                label,
                row.days[index],
              )}
            >
              {label}
            </span>
          ))}
        </div>
      </td>
    </tr>
  );

  if (!rows.length) {
    return (
      <div className="schedule-chk-empty">Нет сводки за выбранную неделю</div>
    );
  }

  return (
    <div className="chk-summary">
      <p className="schedule-chk-hint mb-3">
        Часы и число смен — только типы 1–3 с понедельника по воскресенье.
        Выходные, больничные и отпуска не входят. Факт — из отметок БК за день,
        без обрезки окном смены, обед 4 мин/час.
      </p>
      <div className="chk-summary-kpis">
        <div className="chk-summary-kpi">
          <span>В графике</span>
          <strong>{stats.people}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Всего часов</span>
          <strong>{stats.totalHours}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Всего факт</span>
          <strong>{formatChkHours(stats.totalFact)}</strong>
        </div>
        <div className="chk-summary-kpi">
          <span>Средние часы</span>
          <strong>{stats.average}</strong>
        </div>
        <div className="chk-summary-kpi is-low">
          <span>Недобор (&lt; {LOW_HOURS} ч)</span>
          <strong>{stats.lowCount}</strong>
        </div>
      </div>

      <div className="chk-summary-scale" aria-label="Шкала часов за неделю">
        <span className="chk-summary-scale-item">
          <span className="chk-summary-badge is-low">мало</span>
          меньше {LOW_HOURS} ч
        </span>
        <span className="chk-summary-scale-item">
          <span className="chk-summary-badge is-ok">норма</span>
          {LOW_HOURS}–{HIGH_HOURS} ч
        </span>
        <span className="chk-summary-scale-item">
          <span className="chk-summary-badge is-high">много</span>
          больше {HIGH_HOURS} ч
        </span>
      </div>

      <div className="chk-summary-toolbar">
        <input
          type="search"
          className="form-control form-control-sm"
          placeholder="Поиск по ФИО или должности"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className="chk-summary-toggle">
          <input
            type="checkbox"
            checked={groupByPost}
            onChange={(event) => setGroupByPost(event.target.checked)}
          />
          По должностям
        </label>
      </div>

      <div className="table-responsive chk-summary-table-wrap">
        <table className="table table-bordered schedule-chk-table chk-summary-table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort("name")}>
                  Сотрудник{sortMark("name")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("shifts")}>
                  Смены{sortMark("shifts")}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort("hours")}>
                  Часы{sortMark("hours")}
                </button>
              </th>
              <th className="text-center">
                <button type="button" onClick={() => toggleSort("fact")}>
                  Факт{sortMark("fact")}
                </button>
              </th>
              <th className="text-center">Статус</th>
              <th>Неделя</th>
            </tr>
          </thead>
          {groupByPost ? (
            grouped.length ? (
              grouped.map(([post, postRows]) => (
                <tbody key={post}>
                  <tr className="chk-summary-group">
                    <td colSpan={6}>
                      {post}
                      <span>
                        {postRows.length} чел. ·{" "}
                        {postRows.reduce((sum, row) => sum + row.hours, 0)} ч
                        {postRows.some((row) => row.factHours > 0)
                          ? ` · факт ${formatChkHours(
                              Math.round(
                                postRows.reduce(
                                  (sum, row) => sum + row.factHours,
                                  0,
                                ) * 10,
                              ) / 10,
                            )}`
                          : ""}
                      </span>
                    </td>
                  </tr>
                  {postRows.map(renderRow)}
                </tbody>
              ))
            ) : (
              <tbody>
                <tr>
                  <td colSpan={6} className="chk-summary-empty">
                    Никого не найдено
                  </td>
                </tr>
              </tbody>
            )
          ) : (
            <tbody>
              {filtered.length ? (
                filtered.map(renderRow)
              ) : (
                <tr>
                  <td colSpan={6} className="chk-summary-empty">
                    Никого не найдено
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default ScheduleChkSummary;
